<template>
  <div class="w-100 grid-container">
    <configuration-section-header
      class="my-0"
      :section-icon="'far fa-map-marked'"
      :section-title="$tc('configuration.sections.provinces', 2)"
      :section-subtitle="msgSelectedRows"
      @changedPageSize="changedPageSize($event)"
      @changedMode="changedMode($event)"
    >
      <b-button
        v-tooltip="$t('configuration.actions.activate')"
        :disabled="!canActivateBatch"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="activateDeactivateSelection(true, selectedRows)"
      >
        <i
          class="far fa-check-circle"
          aria-hidden="true"
        />
      </b-button>
      <b-button
        v-tooltip="$t('configuration.actions.deactivate')"
        :disabled="!canDeactivateBatch"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="activateDeactivateSelection(false, selectedRows)"
      >
        <i
          class="far fa-times-circle"
          aria-hidden="true"
        />
      </b-button>
      <div
        style="width:1px; height:18px; background: #d0d0d0"
        class="mx-3"
      />
      <b-button
        v-shortkey="['alt', 'del']"
        v-tooltip="$t('configuration.actions.delete')"
        :disabled="selectedRows.length < 1"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="deleteProvinces(selectedRows)"
        @shortkey="deleteProvinces(selectedRows)"
      >
        <i
          class="far fa-trash-alt"
          aria-hidden="true"
        />
      </b-button>
      <b-button
        v-shortkey="['alt', 'e']"
        v-tooltip="$t('configuration.actions.update')"
        :disabled="selectedRows.length !== 1"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="showProvinceModal($t('configuration.actions.update'), selectedRows[0])"
        @shortkey="showProvinceModal($t('configuration.actions.update'), selectedRows[0])"
      >
        <i
          class="far fa-edit"
          aria-hidden="true"
        />
      </b-button>
      <b-button
        v-shortkey="['ctrl', 'enter']"
        v-tooltip="$t('configuration.actions.add')"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="showProvinceModal($t('configuration.actions.add'))"
        @shortkey="showProvinceModal($t('configuration.actions.add'))"
      >
        <i
          class="far fa-plus"
          aria-hidden="true"
        />
      </b-button>
    </configuration-section-header>
    <div class="flex-grow-1">
      <vt-grid
        pagination
        class="configuration-grid"
        :grid-options="gridOptions"
        :column-defs="columnDefs"
        :component-code="gridUiComponent"
      />
    </div>
    <modal-wrapper ref="provinceModal">
      <province-modal
        :action-title="actionTitle"
        @update="patchProvinces($event)"
        @create="createProvince($event)"
      />
    </modal-wrapper>
  </div>
</template>

<script>
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader'
import VtGrid from '@/components/VtGrid/VtGrid'
import columnsProvincesGrid from '@/modules/configurationModule/detail/demography/provinces/columnsProvincesGrid'
import ProvinceModal from '@/modules/configurationModule/detail/demography/provinces/ProvinceModal'
import gridSideBarConfig from '@/grid/gridSideBarConfig'
import { PaginatorDatasource } from '@/shared/resources/datasources/paginator/paginator-datasource'
import ProvinceConfigurationServices from '@/api/resources/configuration/demography/province/ProvinceConfigurationServices'

export default {
    name: 'DemographyProvincesGrid',
    components: {
        ConfigurationSectionHeader,
        VtGrid,
        ProvinceModal
    },
    data() {
        return {
            gridUiComponent: 'uicomponent.configuration.global.demography.provinces',
            actionTitle: null,
            selectedRows: [],
            columnDefs: columnsProvincesGrid(),
            mode: {
                size: 25
            }
        }
    },
    computed: {
        msgSelectedRows() {
            let msg = `${this.selectedRows.length} ${this.$tc('global.selectedElements', this.selectedRows.length)}`
            if (this.selectedRows.length === 0) msg = ''
            return msg
        },
        rowHeight() {
            return this.mode?.size
        },
        gridOptions() {
            return {
                sideBar: gridSideBarConfig,
                getContextMenuItems: this.getContextMenuItems,
                onGridReady: this.onGridReady,
                onSelectionChanged: this.onSelectionChanged,
                paginationPageSize: 20,
                paginationAutoPageSize: false,
                getRowHeight: () => this.rowHeight
            }
        },
        hasActiveProvince() {
            return !!this.selectedRows.find(province => province.active)
        },
        hasDeactiveProvince() {
            return !!this.selectedRows.find(province => !province.active)
        },
        hasAllActivated() {
            return this.selectedRows.length === (this.selectedRows.filter(province => province.active).length)
        },
        hasAllDeactivated() {
            return this.selectedRows.length === (this.selectedRows.filter(province => !province.active).length)
        },
        canDeactivateBatch() {
            return this.selectedRows.length && this.hasActiveProvince && !this.hasAllDeactivated
        },
        canActivateBatch() {
            return this.hasDeactiveProvince && !this.hasAllActivated
        }
    },
    methods: {
        async activateDeactivateSelection(option, selectionBatch) {
            const provincesToPatch = selectionBatch.filter(province => province.active !== option).map(province => {
                return {
                    active: option,
                    id: province.id
                }
            })
            if (provincesToPatch.length) {
                this.updateLoading(true)
                const [err] = await this.toCall(ProvinceConfigurationServices.patchProvincesBatch(provincesToPatch))
                this.updateLoading(false)
                if (err) {
                    this.showErrorModalGlobal()
                    return
                }
                if (provincesToPatch.length > 1) this.showToastSuccessGlobal(this.$tc('configuration.toastMessages.updated', 2, { elements: provincesToPatch.length }))
                else this.showToastSuccessGlobal(this.$tc('configuration.toastMessages.updated', 1))
                this.reloadData()
            }
        },
        async patchProvinces(...provinces) {
            const response = await this.$_toCallUnique(ProvinceConfigurationServices.patchProvincesBatch(provinces))
            if (response) {
                this.$refs.provinceModal.hide()
                this.showToastSuccessGlobal(this.$tc('configuration.toastMessages.updated', 1))
                this.reloadData()
            }
        },
        async createProvince(province) {
            const response = await this.$_toCallUnique(ProvinceConfigurationServices.createProvince(province))
            if (response) {
                this.$refs.provinceModal.hide()
                this.showToastSuccessGlobal(this.$t('configuration.toastMessages.created'))
                this.reloadData()
            }
        },
        async deleteProvincesBatch(provinces) {
            const provincesToDelete = provinces.map(province => province.id)
            this.updateLoading(true)
            const [err, response] = await this.toCall(ProvinceConfigurationServices.deleteProvincesBatch(provincesToDelete))
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobal()
                return
            } else if (response.length) {
                this.handleErrors(response)
                return
            }
            if (provincesToDelete.length > 1) this.showToastSuccessGlobal(this.$tc('configuration.toastMessages.deleted', 2, { elements: provincesToDelete.length }))
            else this.showToastSuccessGlobal(this.$tc('configuration.toastMessages.deleted', 1))
            this.reloadData()
        },
        reloadData() {
            this.gridApi.deselectAll()
            this.gridApi.setServerSideDatasource(new PaginatorDatasource(ProvinceConfigurationServices))
        },
        getContextMenuItems(params) {
            if (!params?.node) {
                return []
            } else {
                return [
                    {
                        name: this.$t('configuration.actions.update'),
                        cssClasses: this.selectedRows.length > 1 ? ['not-show'] : '',
                        action: () => this.showProvinceModal(this.$t('configuration.actions.update'), params?.node.data)
                    },
                    {
                        name: this.$t('configuration.actions.delete'),
                        cssClasses: this.selectedRows.length < 1 ? ['not-show'] : '',
                        action: () => this.deleteProvinces(this.selectedRows)
                    },
                    {
                        name: params?.node.data.active ? this.$t('configuration.actions.deactivate') : this.$t('configuration.actions.activate'),
                        cssClasses: this.selectedRows.length ? ['not-show'] : '',
                        action: () => this.activateDeactivateSelection(!params.node?.data.active, [params?.node.data])
                    },
                    {
                        name: this.$t('configuration.actions.activate'),
                        cssClasses: this.canActivateBatch ? '' : ['not-show'],
                        action: () => this.activateDeactivateSelection(true, this.selectedRows)
                    },
                    {
                        name: this.$t('configuration.actions.deactivate'),
                        cssClasses: this.canDeactivateBatch ? '' : ['not-show'],
                        action: () => this.activateDeactivateSelection(false, this.selectedRows)
                    }
                ]
            }
        },
        onSelectionChanged({ api }) {
            this.selectedRows = api.getSelectedRows()
        },
        onGridReady({ api }) {
            this.gridApi = api
            api.setServerSideDatasource(new PaginatorDatasource(ProvinceConfigurationServices))
        },
        showProvinceModal(title, selectedProvince) {
            this.actionTitle = title
            this.$refs.provinceModal.show([selectedProvince])
        },
        async deleteProvinces(provinces) {
            if (provinces.length) {
                const promise = await this.showConfirmationModalGlobal(`${this.$t('configuration.grid.deleteConfirmation')} ${this.selectedRows.length} ${this.$tc('configuration.grid.element', this.selectedRows.length)}, <br/> ${this.$t('configuration.grid.deleteQuestion')}`, this.$t('configuration.actions.delete'))
                if (promise) {
                    await this.deleteProvincesBatch(provinces)
                }
            }
        },
        changedPageSize(pageSize) {
            this.gridApi.paginationSetPageSize(pageSize)
        },
        changedMode(selectedOption) {
            this.mode = selectedOption
            this.reloadData()
        }
    }
}
</script>
